import { useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SEGMENT_LABEL } from "@/lib/segments";
import { Loader2 } from "lucide-react";
import CommentsDialog from "@/components/dashboard/CommentsDialog";
import ProfileDetailDialog from "@/components/dashboard/ProfileDetailDialog";
import AISearchForm from "@/components/dashboard/ai/AISearchForm";
import AISearchFilters from "@/components/dashboard/ai/AISearchFilters";
import AIResultCard from "@/components/dashboard/ai/AIResultCard";

// Orchestrator: owns state + data-fetching + client-side filtering, and composes
// the extracted presentational pieces (form / filters / result cards).
export default function AISearch() {
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [active, setActive] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [fCity, setFCity] = useState("");
  const [fSource, setFSource] = useState("");
  const [fScore, setFScore] = useState(0);
  const [fPhone, setFPhone] = useState(false);

  const search = async (q) => {
    const text = (typeof q === "string" ? q : query).trim();
    if (!category) { toast.error("Selecione primeiro a categoria (Mulheres, Trans, Homens ou Massagens)"); return; }
    if (text.length < 3) { toast.error("Descreve melhor o que procuras"); return; }
    setQuery(text);
    setLoading(true);
    setMessage("");
    setResults(null);
    setFCity(""); setFSource(""); setFScore(0); setFPhone(false);
    try {
      const { data } = await api.post("/ai/search", { query: text, category });
      setResults(data.results);
      if (data.message) setMessage(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro na pesquisa");
    } finally {
      setLoading(false);
    }
  };

  const cities = useMemo(() => [...new Set((results || []).map((r) => r.city).filter(Boolean))].sort(), [results]);
  const srcs = useMemo(() => {
    const m = new Map(); (results || []).forEach((r) => m.set(r.source, r.source_name)); return [...m.entries()];
  }, [results]);
  const filtered = useMemo(() => (results || []).filter((r) =>
    (!fCity || r.city === fCity) && (!fSource || r.source === fSource) && (r.score || 0) >= fScore && (!fPhone || r.phone)), [results, fCity, fSource, fScore, fPhone]);

  return (
    <div className="space-y-6" data-testid="ai-search">
      <AISearchForm
        category={category} setCategory={setCategory}
        query={query} setQuery={setQuery}
        loading={loading} onSearch={search}
      />

      {loading && (
        <div className="text-center py-12 text-[#71717a] text-sm flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 spin text-[#c8aa6e]" />
          A IA está a analisar os perfis de {SEGMENT_LABEL[category]}…
        </div>
      )}

      {message && !loading && (
        <div className="bg-[#18181b] border border-[#27272a] px-4 py-8 text-center text-[#71717a] text-sm">{message}</div>
      )}

      {results && results.length > 0 && !loading && (
        <div className="space-y-4">
          <AISearchFilters
            cities={cities} srcs={srcs}
            fCity={fCity} setFCity={setFCity}
            fSource={fSource} setFSource={setFSource}
            fScore={fScore} setFScore={setFScore}
            fPhone={fPhone} setFPhone={setFPhone}
            filteredCount={filtered.length} totalCount={results.length}
            categoryLabel={SEGMENT_LABEL[category]}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="ai-results-grid">
            {filtered.map((p, i) => <AIResultCard key={p.id} p={p} i={i} onDetail={setDetailId} onNotes={setActive} />)}
          </div>
          {filtered.length === 0 && (
            <div className="bg-[#18181b] border border-[#27272a] px-4 py-8 text-center text-[#71717a] text-sm">Nenhum resultado com estes filtros.</div>
          )}
        </div>
      )}

      {results && results.length === 0 && !message && !loading && (
        <div className="bg-[#18181b] border border-[#27272a] px-4 py-8 text-center text-[#71717a] text-sm">
          Nenhum perfil relevante encontrado para esta pesquisa.
        </div>
      )}

      <CommentsDialog profile={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />
      <ProfileDetailDialog profileId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
